import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  Input,
  Avatar,
  AvatarFallback,
  AvatarImage,
  LoadingState,
} from "@tabipla/ui";
import {
  Users,
  AlertCircle,
  CheckCircle2,
  Wallet,
  Shield,
  Clock,
  Zap,
  ArrowRight,
  MapPin,
  Calendar,
  UserPlus,
  LogIn,
  ChevronLeft,
} from "lucide-react";
import {
  useInvitationInfo,
  useJoinAsGuest,
  useHandlePendingInvitation,
} from "../../application/hooks/useInvitations";
import { useAuth } from "../../application/hooks/useAuth";

export function JoinGroupPage() {
  const { t } = useTranslation();
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();

  const { data: invitationInfo, isLoading, error } = useInvitationInfo(code);
  const joinMutation = useJoinAsGuest(code ?? "");
  const { isLoading: isAuthLoading } = useAuth();
  const { setPendingInvitation } = useHandlePendingInvitation();

  const [name, setName] = useState("");
  const [isJoined, setIsJoined] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Format trip dates
  const formatTripDates = (startDate: string | null, endDate: string | null): string | null => {
    if (!startDate || !endDate) return null;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const formatDate = (d: Date) => `${d.getMonth() + 1}/${d.getDate()}`;
    return t("invitations.tripDates", { start: formatDate(start), end: formatDate(end) });
  };

  // Calculate remaining time until expiry
  const getExpiryText = (expiresAt: string | null): string | null => {
    if (!expiresAt) return null;
    const expiry = new Date(expiresAt);
    const now = new Date();
    const diffMs = expiry.getTime() - now.getTime();
    if (diffMs <= 0) return null;

    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      return t("invitations.expiresInDays", { days: diffDays });
    }
    return t("invitations.expiresInHours", { hours: diffHours });
  };

  const handleNameSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setShowConfirmation(true);
  };

  const handleJoinAsGuest = async () => {
    try {
      const result = await joinMutation.mutateAsync({ name: name.trim() });
      if (!result) return;

      // Token storage is handled by the useJoinAsGuest hook

      setIsJoined(true);

      // Redirect to the group page after a short delay
      // Use window.location.href instead of navigate() to force a full page reload
      // This ensures Better Auth picks up the new session cookie
      setTimeout(() => {
        window.location.href = `/groups/${result.group.id}`;
      }, 2000);
    } catch {
      // Error is handled by the mutation
    }
  };

  const handleLoginAndJoin = () => {
    // Store the invitation code to join after login
    setPendingInvitation(code ?? "");
    navigate("/login");
  };

  if (isLoading || isAuthLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-emerald-50 via-background to-emerald-100 p-4 dark:from-emerald-950 dark:via-background dark:to-emerald-900">
        <LoadingState message={t("invitations.loading")} />
      </div>
    );
  }

  if (error || !invitationInfo) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-emerald-50 via-background to-emerald-100 p-4 dark:from-emerald-950 dark:via-background dark:to-emerald-900">
        <div className="animate-scale-in w-full max-w-md">
          <Card className="border-0 shadow-xl">
            <CardContent className="pt-8">
              <div className="flex flex-col items-center text-center">
                <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
                  <AlertCircle className="h-8 w-8 text-red-500" />
                </div>
                <h2 className="mb-2 text-xl font-semibold">{t("invitations.notFound")}</h2>
                <p className="text-muted-foreground">{t("invitations.notFoundDescription")}</p>
                <Button
                  className="mt-6 bg-emerald-500 hover:bg-emerald-600"
                  size="lg"
                  onClick={() => navigate("/")}
                >
                  {t("common.backToHome")}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!invitationInfo.isValid) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-emerald-50 via-background to-emerald-100 p-4 dark:from-emerald-950 dark:via-background dark:to-emerald-900">
        <div className="animate-scale-in w-full max-w-md">
          <Card className="border-0 shadow-xl">
            <CardContent className="pt-8">
              <div className="flex flex-col items-center text-center">
                <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-yellow-100 dark:bg-yellow-900/30">
                  <AlertCircle className="h-8 w-8 text-yellow-500" />
                </div>
                <h2 className="mb-2 text-xl font-semibold">{t("invitations.expired")}</h2>
                <p className="text-muted-foreground">{t("invitations.expiredDescription")}</p>
                <Button
                  className="mt-6 bg-emerald-500 hover:bg-emerald-600"
                  size="lg"
                  onClick={() => navigate("/")}
                >
                  {t("common.backToHome")}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (isJoined) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-emerald-50 via-background to-emerald-100 p-4 dark:from-emerald-950 dark:via-background dark:to-emerald-900">
        <div className="animate-scale-in w-full max-w-md">
          <Card className="border-0 shadow-xl">
            <CardContent className="pt-8">
              <div className="flex flex-col items-center text-center">
                <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
                  <CheckCircle2 className="h-8 w-8 text-green-500" />
                </div>
                <h2 className="mb-2 text-xl font-semibold">{t("invitations.joinedSuccess")}</h2>
                <p className="text-muted-foreground">
                  {t("invitations.redirecting", { groupName: invitationInfo.groupName })}
                </p>

                {/* What to do next hint */}
                <div className="mt-6 w-full rounded-lg bg-emerald-50 p-4 text-left dark:bg-emerald-900/20">
                  <p className="mb-2 text-sm font-medium text-emerald-700 dark:text-emerald-300">
                    {t("invitations.whatNext", "これから何ができる？")}
                  </p>
                  <ul className="space-y-2 text-sm text-emerald-600 dark:text-emerald-400">
                    <li className="flex items-center gap-2">
                      <ArrowRight className="h-3.5 w-3.5 shrink-0" />
                      {t("invitations.nextTip1", "残高を確認して、自分がいくら払うか見る")}
                    </li>
                    <li className="flex items-center gap-2">
                      <ArrowRight className="h-3.5 w-3.5 shrink-0" />
                      {t("invitations.nextTip2", "払ったら「払いました」ボタンで報告")}
                    </li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Confirmation step - choose between guest and account
  if (showConfirmation) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-emerald-50 via-background to-emerald-100 p-4 dark:from-emerald-950 dark:via-background dark:to-emerald-900">
        <div className="animate-scale-in w-full max-w-md">
          {/* Logo */}
          <div className="mb-6 text-center">
            <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-500 shadow-lg">
              <Wallet className="h-7 w-7 text-white" />
            </div>
            <h1 className="text-xl font-bold">Waritto</h1>
          </div>

          <Card className="border-0 shadow-xl">
            <CardHeader className="pb-4">
              <button
                onClick={() => setShowConfirmation(false)}
                className="mb-2 flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
              >
                <ChevronLeft className="h-4 w-4" />
                {t("common.back")}
              </button>
              <CardTitle className="text-center text-lg">
                {t("invitations.confirmTitle", "参加方法を選んでください")}
              </CardTitle>
              <CardDescription className="text-center">
                {t("invitations.confirmDescription", "「{{name}}」さんとして参加します", {
                  name: name.trim(),
                })}
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-3">
              {/* Guest Option */}
              <button
                onClick={handleJoinAsGuest}
                disabled={joinMutation.isPending}
                className="w-full rounded-xl border-2 border-emerald-200 bg-emerald-50 p-4 text-left transition-all hover:border-emerald-400 hover:bg-emerald-100 active:scale-[0.98] disabled:opacity-50 dark:border-emerald-800 dark:bg-emerald-900/20 dark:hover:border-emerald-600 dark:hover:bg-emerald-900/40"
              >
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white">
                    <UserPlus className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-emerald-700 dark:text-emerald-300">
                      {joinMutation.isPending
                        ? t("invitations.joining")
                        : t("invitations.guestJoin", "ゲストで参加")}
                    </p>
                    <p className="mt-1 text-sm text-emerald-600 dark:text-emerald-400">
                      {t(
                        "invitations.guestJoinDesc",
                        "登録不要・すぐ参加できます。このグループ限定で使えます。"
                      )}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <span className="inline-flex items-center rounded-full bg-emerald-200 px-2 py-0.5 text-xs font-medium text-emerald-800 dark:bg-emerald-800 dark:text-emerald-200">
                        <Zap className="mr-1 h-3 w-3" />
                        {t("invitations.instant", "即時参加")}
                      </span>
                      <span className="inline-flex items-center rounded-full bg-emerald-200 px-2 py-0.5 text-xs font-medium text-emerald-800 dark:bg-emerald-800 dark:text-emerald-200">
                        {t("invitations.noRegistration", "登録不要")}
                      </span>
                    </div>
                  </div>
                </div>
              </button>

              {/* Account Option */}
              <button
                onClick={handleLoginAndJoin}
                disabled={joinMutation.isPending}
                className="w-full rounded-xl border-2 border-border bg-card p-4 text-left transition-all hover:border-primary/50 hover:bg-accent active:scale-[0.98] disabled:opacity-50"
              >
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
                    <LogIn className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold">
                      {t("invitations.accountJoin", "アカウントで参加")}
                    </p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {t(
                        "invitations.accountJoinDesc",
                        "ログインまたは新規登録して参加。複数グループで使えます。"
                      )}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                        <Shield className="mr-1 h-3 w-3" />
                        {t("invitations.dataSync", "データ同期")}
                      </span>
                      <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                        {t("invitations.multiGroup", "複数グループ対応")}
                      </span>
                    </div>
                  </div>
                </div>
              </button>

              {joinMutation.isError && (
                <div className="flex items-center gap-2 rounded-xl bg-destructive/10 p-3 text-sm text-destructive">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  {t("invitations.joinError")}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-emerald-50 via-background to-emerald-100 p-4 dark:from-emerald-950 dark:via-background dark:to-emerald-900">
      <div className="animate-scale-in w-full max-w-md">
        {/* Logo */}
        <div className="mb-6 text-center">
          <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-500 shadow-lg">
            <Wallet className="h-7 w-7 text-white" />
          </div>
          <h1 className="text-xl font-bold">Waritto</h1>
        </div>

        <Card className="border-0 shadow-xl">
          {/* Rich Preview Header */}
          <CardHeader className="pb-4">
            {/* Inviter Info */}
            <div className="mb-4 flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Avatar className="h-6 w-6">
                <AvatarImage src={invitationInfo.invitedBy.image ?? undefined} />
                <AvatarFallback className="text-xs">
                  {invitationInfo.invitedBy.name.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <span>
                <strong className="text-foreground">{invitationInfo.invitedBy.name}</strong>
                {t("invitations.invitedYou")}
              </span>
            </div>

            {/* Group Info */}
            <div className="text-center">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/30">
                <Users className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
              </div>
              <CardTitle className="text-lg">
                {t("invitations.joinTitle", { groupName: invitationInfo.groupName })}
              </CardTitle>
              <CardDescription className="mt-1">{t("invitations.joinDescription")}</CardDescription>
            </div>

            {/* Member Preview */}
            {invitationInfo.members.length > 0 && (
              <div className="mt-4 flex items-center justify-center">
                <div className="flex -space-x-2">
                  {invitationInfo.members.slice(0, 4).map((member) => (
                    <Avatar key={member.id} className="h-8 w-8 border-2 border-background">
                      <AvatarImage src={member.image ?? undefined} />
                      <AvatarFallback className="bg-emerald-100 text-xs dark:bg-emerald-900/30">
                        {member.name.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                  ))}
                  {invitationInfo.memberCount > 4 && (
                    <div className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-background bg-muted text-xs font-medium">
                      +{invitationInfo.memberCount - 4}
                    </div>
                  )}
                </div>
                <span className="ml-3 text-sm text-muted-foreground">
                  {t("invitations.memberCount", { count: invitationInfo.memberCount })}
                </span>
              </div>
            )}

            {/* Trip Info (if linked to a trip) */}
            {invitationInfo.trip && (
              <div className="mt-4 rounded-lg bg-blue-50 p-3 dark:bg-blue-900/20">
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-xl">{invitationInfo.trip.coverEmoji ?? "✈️"}</span>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-blue-600 dark:text-blue-400">
                      {t("invitations.linkedTrip")}
                    </p>
                    <p className="font-medium text-blue-700 dark:text-blue-300">
                      {invitationInfo.trip.title}
                    </p>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-blue-600 dark:text-blue-400">
                      <span className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {invitationInfo.trip.destination}
                      </span>
                      {formatTripDates(
                        invitationInfo.trip.startDate,
                        invitationInfo.trip.endDate
                      ) && (
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatTripDates(
                            invitationInfo.trip.startDate,
                            invitationInfo.trip.endDate
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Expiry Info */}
            {getExpiryText(invitationInfo.expiresAt) && (
              <div className="mt-3 flex items-center justify-center gap-1.5 text-xs text-amber-600 dark:text-amber-400">
                <Clock className="h-3.5 w-3.5" />
                {getExpiryText(invitationInfo.expiresAt)}
              </div>
            )}
          </CardHeader>

          <CardContent>
            <form onSubmit={handleNameSubmit} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">{t("invitations.yourName")}</label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t("invitations.namePlaceholder")}
                  required
                  disabled={joinMutation.isPending}
                />
              </div>

              {joinMutation.isError && (
                <div className="flex items-center gap-2 rounded-xl bg-destructive/10 p-3 text-sm text-destructive">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  {t("invitations.joinError")}
                </div>
              )}

              <Button
                type="submit"
                className="w-full bg-emerald-500 hover:bg-emerald-600"
                size="lg"
                disabled={joinMutation.isPending || !name.trim()}
              >
                {joinMutation.isPending ? t("invitations.joining") : t("invitations.join")}
              </Button>
            </form>

            <div className="mt-6 border-t pt-6">
              <p className="mb-4 text-center text-sm text-muted-foreground">
                {t("invitations.haveAccount")}
              </p>
              <Button variant="outline" className="w-full" size="lg" onClick={handleLoginAndJoin}>
                {t("invitations.loginAndJoin")}
              </Button>
            </div>

            {/* Benefits highlight */}
            <div className="mt-6 grid grid-cols-3 gap-2 rounded-lg bg-emerald-50/50 p-3 dark:bg-emerald-900/10">
              <div className="flex flex-col items-center gap-1 text-center">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/30">
                  <Clock className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                </div>
                <span className="text-xs font-medium text-emerald-700 dark:text-emerald-300">
                  {t("invitations.quickJoin", "30秒で参加")}
                </span>
              </div>
              <div className="flex flex-col items-center gap-1 text-center">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/30">
                  <Zap className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                </div>
                <span className="text-xs font-medium text-emerald-700 dark:text-emerald-300">
                  {t("invitations.noRegistration", "登録不要")}
                </span>
              </div>
              <div className="flex flex-col items-center gap-1 text-center">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/30">
                  <Shield className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                </div>
                <span className="text-xs font-medium text-emerald-700 dark:text-emerald-300">
                  {t("invitations.secure", "安全")}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
